import React from 'react';
import { TextField, Tooltip, withStyles } from '@material-ui/core';
import Select from 'react-select';
import flowRight from 'lodash.flowright';
import clsx from 'clsx';

import styles, { selectCustomStyles } from '../styles/informationSection';
import { reactSelectDefaultStyles } from '../../../consts/reactSelectDefaultStyles';
import Optional from '../../../utils/optional';
import CustomCheckbox from '../../../partials/CustomCheckbox';
import CustomColorButton from '../../../partials/customButtons/CustomColorButton';
import withSettings from '../../../utils/withSettings';
import { translate } from '../../../i18n/I18nProvider';
import { doesArrayHasLength, isDefined } from '../../../utils/isDefined';
import AvailableNumbers from './AvailableNumbers';

const PHONE_TYPE_OPTIONS = [
  { value: 'OFFICE_FIXED', label: translate('NUMBERS.OFFICE_FIXED') },
  { value: 'OFFICE_MOBILE', label: translate('NUMBERS.OFFICE_MOBILE') },
  { value: 'PRIVATE_FIXED', label: translate('NUMBERS.PRIVATE_FIXED') },
  { value: 'PRIVATE_MOBILE', label: translate('NUMBERS.PRIVATE_MOBILE') }
];

const PhoneLine = ({
  phoneLine,
  label,
  handleChange,
  handleTextfieldBlur,
  numberError,
  classes,
  settings,
  setSelectedNumber,
  line,
  excludeNumbers = [],
  validationErrors = {},
  setFieldError,
  setFieldTouched,
  validateForm
}) => {
  const handleLineChange = ({ field, value }) => {
    handleChange({
      field,
      value,
      line,
      phoneLine
    });
  };

  const handleAliasChange = (index, value, field) => {
    const updatedAliases = phoneLine.aliases.map((alias, i) =>
      i === index ? { ...alias, [field]: value } : alias
    );
    setLineChange(updatedAliases);
  };

  const handleAddAlias = () => {
    const updatedAliases = [
      ...(phoneLine.aliases || []),
      { value: '', short_number: false, id: Date.now() }
    ];
    setLineChange(updatedAliases);
  };

  const handleDeleteAlias = (index, id) => {
    const updatedAliases = phoneLine.aliases.filter((_, i) => i !== index);
    delete numberError[`${line}_alias_${id}`];
    setLineChange(updatedAliases);
  };

  const handleAliasNumberSelection = (selectedNumber, aliasId) => {
    const aliasIndex = phoneLine.aliases.findIndex(
      alias => alias.id === aliasId
    );
    if (aliasIndex !== -1) {
      const numberValue =
        selectedNumber?.value || selectedNumber?.number || selectedNumber;
      handleAliasChange(aliasIndex, numberValue, 'value');
    }
  };

  const getAliasExcludedNumbers = currentAliasId => {
    const aliasExcludedNumbers = [...excludeNumbers];

    if (phoneLine?.value) {
      aliasExcludedNumbers.push(phoneLine.value);
    }

    if (phoneLine?.aliases) {
      phoneLine.aliases.forEach(alias => {
        if (alias.id !== currentAliasId && alias.value) {
          aliasExcludedNumbers.push(alias.value);
        }
      });
    }

    return aliasExcludedNumbers;
  };

  const setLineChange = updatedLine => {
    handleChange({
      field: 'aliases',
      value: updatedLine,
      line,
      phoneLine
    });
  };

  const handleClearLine = () => {
    handleChange({
      field: 'value',
      value: '',
      line,
      phoneLine: {}
    });

    delete numberError[line];

    if (setFieldError) {
      setFieldError(`phoneNumbers.${line}.number_type`, undefined);
    }

    if (setFieldTouched) {
      setFieldTouched(`phoneNumbers.${line}.number_type`, false);
    }

    if (validateForm) {
      setTimeout(() => validateForm(), 0);
    }
  };
  return (
    <div className={classes.numberContainer}>
      <div className={classes.smallLabel}>{label} </div>
      <div className={classes.numberRow}>
        <div className={classes.box}>
          <div>{translate('TABLE.NUMBER')}</div>
          <TextField
            variant="outlined"
            margin="dense"
            value={Optional(phoneLine?.value).or('')}
            onChange={e =>
              handleLineChange({
                field: 'value',
                value: e.target.value,
                line: phoneLine
              })
            }
            onBlur={() =>
              handleTextfieldBlur({
                line
              })
            }
            error={!!numberError[line]}
            helperText={numberError[line]}
            disabled
            InputProps={{
              style: {
                color: 'inherit'
              }
            }}
          />
        </div>
        <div className={classes.box}>
          <div>{translate('USER.LINE_TYPE')}</div>
          <Select
            styles={reactSelectDefaultStyles}
            customStyles={selectCustomStyles}
            value={Optional(phoneLine?.number_type)
              .map(type =>
                PHONE_TYPE_OPTIONS.find(option => option.value === type)
              )
              .or('')}
            options={PHONE_TYPE_OPTIONS}
            onChange={option => {
              handleLineChange({
                field: 'number_type',
                value: option.value,
                line: phoneLine
              });
              if (validateForm) {
                setTimeout(() => validateForm(), 0);
              }
            }}
          />
          {validationErrors?.number_type && (
            <div
              style={{
                color: '#f44336',
                fontSize: '0.75rem',
                marginTop: '8px'
              }}
            >
              {validationErrors.number_type}
            </div>
          )}
        </div>
        <div className={classes.boxRow}>
          <CustomCheckbox
            value={Optional(phoneLine?.mobile_vpn).or(false)}
            onChange={e =>
              handleLineChange({
                field: 'mobile_vpn',
                value: e.target.checked,
                line: phoneLine
              })
            }
          />
          <div className={classes.labelBox}>{`${translate('MOBILE')} VPN`}</div>
          {phoneLine?.value && (
            <Tooltip title={translate('USER.CLEAR_NUMBER')}>
              <i
                style={{
                  color: settings?.colors.modal_header,
                  cursor: 'pointer',
                  marginLeft: '8px'
                }}
                className={clsx('fas fa-trash-alt', classes.deleteIcon)}
                onClick={handleClearLine}
              />
            </Tooltip>
          )}
        </div>
      </div>
      <AvailableNumbers
        setSelectedNumber={selected => {
          const numberValue = selected?.value || selected?.number || selected;
          setSelectedNumber(numberValue, line);
        }}
        excludeNumbers={excludeNumbers}
      />
      {doesArrayHasLength(phoneLine?.aliases) && (
        <div className={classes.aliasesContainer}>
          {phoneLine.aliases.map((alias, index) => (
            <div key={index}>
              <div className={classes.numberRow}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  value={alias.value}
                  onChange={e => {
                    handleAliasChange(index, e.target.value, 'value');
                  }}
                  onBlur={() =>
                    handleTextfieldBlur({
                      line,
                      isAlias: true,
                      index: alias.id
                    })
                  }
                  error={!!numberError[`${line}_alias_${alias.id}`]}
                  helperText={numberError[`${line}_alias_${alias.id}`]}
                  disabled
                  InputProps={{
                    style: {
                      color: 'inherit'
                    }
                  }}
                />
                <div className={classes.aliasBoxRow}>
                  <CustomCheckbox
                    value={Optional(alias?.short_number).or(false)}
                    onChange={e => {
                      handleAliasChange(
                        index,
                        e.target.checked,
                        'short_number'
                      );
                    }}
                  />
                  <div className={classes.labelBox}>
                    {translate('USER.SHORT_NUMBER')}
                  </div>
                  <Tooltip title={translate('USER.DELETE_ALIAS')}>
                    <i
                      style={{ color: settings?.colors.modal_header }}
                      className={clsx('fas fa-trash-alt', classes.deleteIcon)}
                      onClick={() => {
                        handleDeleteAlias(index, alias.id);
                      }}
                    ></i>
                  </Tooltip>
                </div>
              </div>
              <AvailableNumbers
                setSelectedNumber={selected =>
                  handleAliasNumberSelection(selected, alias.id)
                }
                isShortNumber={true}
                excludeNumbers={getAliasExcludedNumbers(alias.id)}
              />
            </div>
          ))}
        </div>
      )}
      <CustomColorButton
        size="small"
        backgroundColor={settings?.colors.modal_header}
        color="#FFF"
        onClick={() => {
          handleAddAlias();
        }}
        disabled={
          !Optional(phoneLine?.value).or(false) || isDefined(numberError[line])
        }
      >
        {translate('USER.ADD_ALIAS')}
      </CustomColorButton>
    </div>
  );
};

export default flowRight(withSettings, withStyles(styles))(PhoneLine);
